import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { DeviceDetectorService } from 'ngx-device-detector';

import { ClozeTestStyleModel, ClozeTestModel, UrlParametersModel, ClozeTestComponent } from 'gast-onset-ng2';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-demo',
  templateUrl: './demo.component.html',
  styleUrls: ['./demo.component.scss'],
  standalone: true,
  imports: [CommonModule, TranslateModule, ClozeTestComponent]
})
export class DemoComponent implements OnInit {

  readonly #deviceService = inject(DeviceDetectorService);
  readonly #route = inject(ActivatedRoute);

  cTestLogo = signal('');
  cTestButtonColor = signal('');
  mobileMode = signal(false);

  isStarted = signal(false);
  validToken = signal(false);
  isLoading = signal(false);

  // onset param
  gastBookingId = signal<number | null>(null);
  startLevel = signal(2);
  endLevel = signal(5);
  variantId = signal(11);
  language = signal(1); // default DE
  lang = signal('de');

  clozeTestModel = signal<ClozeTestModel>({} as ClozeTestModel);
  clozeTestStyleModel = signal<ClozeTestStyleModel>({} as ClozeTestStyleModel);

  ngOnInit() {
    // detect mobile
    this.mobileMode.set(this.#deviceService.isMobile());
    console.log('is mobile device: ' + this.mobileMode());

    // generate style
    this.clozeTestStyleModel.set(this.#generateClozeTestStyle());

    // sett logo and button color
    this.cTestLogo.set('onset-assets/img/onset_logo.png');
    this.cTestButtonColor.set(this.clozeTestStyleModel()!.onsetColor);

    // callback for query parameter
    this.#route.queryParams.subscribe(params => {
      this.isLoading.set(true);
      if (params['token']) {
        const token = params['token'] as string;
        if (token === 'DEMO-91034f05-f5b9-459e-a5d1-03db1215eb59') {
          this.validToken.set(true);
        }
      }
      if (params['lang'] === 'en') {
        this.language.set(2);
        this.lang.set('en');
      }
      this.isLoading.set(false);
    });
  }

  startOnset(): void {
    this.clozeTestModel.set(this.#generateClozeTestModel());
    this.clozeTestStyleModel.set(this.#generateClozeTestStyle());
    this.isStarted.set(true);
  }

  onStartTest(_start: boolean): void {
    console.log(`test started`);
  }

  onBookingInit(gastBookingId: number): void {
    this.gastBookingId.set(gastBookingId);
    console.log(`init gastBookingId ${gastBookingId}`);
  }

  onTestHasBeenSaved(gastBookingId: number): void {
    this.gastBookingId.set(gastBookingId);
    console.log(`saving gastBookingId ${gastBookingId}`);
  }

  onClozeTestCompleted(): void {
    console.log(`test completed`);
  }

  onTestResult(test: any): void {
    console.log(`next test result ${test}`);
  }

  #generateClozeTestModel(): ClozeTestModel {
    const onsetServiceUrl = 'https://apprex.gast.de/gast-service-onset/';

    const clozeTestModel: ClozeTestModel = {} as ClozeTestModel;
    clozeTestModel.getResultsUrl = onsetServiceUrl + 'api/score';
    clozeTestModel.postTestUrl = onsetServiceUrl + 'api/aufgabe/save';
    clozeTestModel.getTestUrl = onsetServiceUrl + 'api/aufgabe';
    clozeTestModel.getNextTestUrl = onsetServiceUrl + 'api/aufgabe/next';
    clozeTestModel.startWithBuchungId = this.gastBookingId() ?? 0;
    clozeTestModel.urlParameters = {
      herkunft: 'deutschland',
      geschlecht: 'm',
      geburtsdatum: '1990-01-01',
      variante: this.variantId(),
      testzentrum: 454,
      sprache: this.language(),
      tool: 1,
      start_level: this.startLevel(),
      end_level: this.endLevel()
    } as UrlParametersModel;
    return clozeTestModel;
  }

  #generateClozeTestStyle(): ClozeTestStyleModel {
    const model = {} as ClozeTestStyleModel;
    model.siteBackground = '#ffffff';
    model.exerciseBackground = '#EEF3F8';
    model.frameColor = '#92aac1';
    model.barColor = '#b3c9de';
    model.specialCharacterListColor = '#b3c9de';
    model.specialChcaracterTextColor = '#000000';
    model.exerciseTextColor = '#000000';
    model.buttonsColor = '#fa9463';
    model.testdafColor = '#57a7d6';
    model.onsetColor = '#a5d8db';
    model.duoColor = '#ed6a5b';
    model.testasColor = '#fac075';
    model.font = 'arial, helvetica, sans-serif';
    model.fontSize = '11pt';
    model.displayLogo = true;

    if (this.lang() === 'de') {
      model.cTestInformationUrl = 'https://de.wikipedia.org/wiki/Gemeinsamer_europ%C3%A4ischer_Referenzrahmen_f%C3%BCr_Sprachen';
    } else if (this.lang() === 'en') {
      model.cTestInformationUrl = 'https://en.wikipedia.org/wiki/Common_European_Framework_of_Reference_for_Languages';
    } else {
      model.cTestInformationUrl = 'https://en.wikipedia.org/wiki/Common_European_Framework_of_Reference_for_Languages';
    }

    return model;
  }
}
